import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChevronLeft, ChevronRight, Loader2, Mail, Search } from "lucide-react";
import SendResetLinkDialog from "@/components/master/SendResetLinkDialog";

const ROLE_OPTIONS = ["", "worker", "approver", "admin", "tenant_admin", "master"];
const STATUS_OPTIONS = ["", "active", "invited", "disabled"];

/**
 * Phase 36 — Searchable member list for a master workspace.
 *
 * No raw sensitive fields are rendered. Explicit allowlist from the
 * backend's `_safe_member` serializer: id, full_name, email, roles,
 * status, auth_mode (derived), provisioned_via, mfa_enabled,
 * kiosk_pin_set, kiosk_pin_locked. No password_hash, no pin_hash, no
 * mfa_secret, no locked_until timestamp.
 */
export default function MembersTable({ tenantId, tenantSlug }) {
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 25 });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null); // { user_id, email, auth_mode }

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (q.trim()) params.q = q.trim();
      if (role) params.role = role;
      if (status) params.status = status;
      const { data } = await api.get(`/master/tenants/${tenantId}/members`, { params });
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);

  const onSearch = (e) => {
    e.preventDefault();
    setPage(1);
    load();
  };

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / (data.page_size || pageSize)));

  return (
    <div className="space-y-4" data-testid="members-table">
      <form onSubmit={onSearch} className="flex flex-wrap gap-2 rounded-xl border bg-card p-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by email prefix or name substring"
            className="pl-8"
            data-testid="members-search-input"
          />
        </div>
        <Select value={role} onValueChange={(v) => { setRole(v === "__ANY__" ? "" : v); setPage(1); }}>
          <SelectTrigger className="w-[150px]" data-testid="members-role-filter">
            <SelectValue placeholder="Any role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__ANY__">Any role</SelectItem>
            {ROLE_OPTIONS.filter(Boolean).map((r) => (
              <SelectItem key={r} value={r}>{r}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => { setStatus(v === "__ANY__" ? "" : v); setPage(1); }}>
          <SelectTrigger className="w-[150px]" data-testid="members-status-filter">
            <SelectValue placeholder="Any status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__ANY__">Any status</SelectItem>
            {STATUS_OPTIONS.filter(Boolean).map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button type="submit" variant="secondary" data-testid="members-search-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
        </Button>
      </form>

      <div className="overflow-hidden rounded-xl border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Login email</TableHead>
              <TableHead>Roles</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Auth</TableHead>
              <TableHead>MFA</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-sm text-muted-foreground">
                  {loading ? "Loading…" : "No members match these filters."}
                </TableCell>
              </TableRow>
            )}
            {data.items.map((m) => {
              // Defect 3 UX gap — dangling identity rows surface
              // `email=null` + `auth_mode="action_needed"` + `issue="missing_identity"`.
              // Render the membership email (so the admin can still see
              // who the row refers to) and disable the Send-reset action
              // because the backend will 500 anyway (identity_integrity_error).
              const dangling = m.auth_mode === "action_needed";
              const emailShown = m.email || m.membership_email || "—";
              return (
              <TableRow key={m.id} data-testid={`member-row-${m.id}`}>
                <TableCell className="font-medium">{m.full_name || <em className="text-muted-foreground">—</em>}</TableCell>
                <TableCell className="font-mono text-xs">
                  <span data-testid={`member-email-${m.id}`}>{emailShown}</span>
                  {dangling && (
                    <Badge variant="outline" className="ml-2 border-destructive text-destructive text-[10px] uppercase" data-testid={`member-dangling-badge-${m.id}`}>
                      missing identity
                    </Badge>
                  )}
                  {!dangling && m.membership_email && m.membership_email !== m.email && (
                    <span className="ml-2 text-[10px] text-muted-foreground" title="Membership email differs from canonical identity email">
                      membership: {m.membership_email}
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {(m.roles || []).map((r) => (
                      <Badge key={r} variant="outline" className="text-[10px] uppercase tracking-widest">{r}</Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={m.status === "active" ? "default" : "outline"}>{m.status}</Badge>
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={m.auth_mode === "native" ? "bg-primary/10 text-primary" : "bg-muted"}
                    data-testid={`member-auth-mode-${m.id}`}
                  >
                    {m.auth_mode}
                  </Badge>
                </TableCell>
                <TableCell>
                  {m.mfa_enabled ? <Badge className="bg-secondary/15 text-secondary">on</Badge> : <span className="text-muted-foreground">—</span>}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={dangling}
                    title={dangling ? "This member's identity row is missing. Run the identity-materialization helper from master ops before issuing a reset." : undefined}
                    onClick={() => !dangling && setSelected({ user_id: m.id, email: m.email, auth_mode: m.auth_mode })}
                    data-testid={`member-send-reset-${m.id}`}
                  >
                    <Mail className="mr-1 h-3.5 w-3.5" />
                    Send reset link
                  </Button>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <div data-testid="members-total">
          {data.total} total · page {data.page} of {totalPages}
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            data-testid="members-page-prev"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            data-testid="members-page-next"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <SendResetLinkDialog
        open={!!selected}
        onClose={() => setSelected(null)}
        tenantId={tenantId}
        tenantSlug={tenantSlug}
        member={selected}
      />
    </div>
  );
}
